import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { eliminarUsuario } from "../../utils/acciones";
import { getHomeRouteByRole } from "../../utils/rutas";
import { matchesUserTextSearch, userEnrolledInCourseId, userMatchesRoleFilter } from "../../utils/adminUserListFilter";
import { getMessage } from "../../data/locales";
import { extractMongoObjectIdHex } from "../../utils/helpers";
import { fetchAllStaffUsers } from "../../utils/fetchAllStaffUsers";
import ResponsiveActionSidebar from "../../components/ResponsiveActionSidebar";
import ViewportSelect from "../../components/ViewportSelect";
import { API_URL } from "../../config";
import "./CentroControlDirector.css";

const CentroControlDirector = () => {
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [usuariosLoaded, setUsuariosLoaded] = useState(false);
  const [usuariosFetchError, setUsuariosFetchError] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [mensajeListadoCursos, setMensajeListadoCursos] = useState("");
  const [cursos, setCursos] = useState([]);
  const [cursoFiltroId, setCursoFiltroId] = useState("");
  const [rolFiltro, setRolFiltro] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalAction, setModalAction] = useState(""); // "registrar", "cambiarRoles", "estadisticas", "GestorUsuarios", "eliminar"
  const userListRef = useRef(null);
  const [rol, setRol] = useState("");

  useEffect(() => {
    fetch(`${API_URL}/verificar-token`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data.ok && data.usuario && typeof data.usuario.rol === "string") {
          setRol(data.usuario.rol);
        }
      })
      .catch((err) => console.error("Error obteniendo rol:", err));
  }, []);

  // UseEffect para deseleccionar usuario al hacer clic fuera de la lista
  useEffect(() => {
    const handleClickOutsideList = (event) => {
      if (userListRef.current && !userListRef.current.contains(event.target)) {
        setSelectedUser(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideList);
    return () => document.removeEventListener("mousedown", handleClickOutsideList);
  }, []);


  useEffect(() => {
    obtenerUsuarios();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const cargarCursos = async () => {
      try {
        const respuesta = await fetch(`${API_URL}/cursos`, {
          method: "GET",
          credentials: "include",
          signal,
        });
        if (signal.aborted) {
          return;
        }
        let datos;
        let parseFailed = false;
        try {
          datos = await respuesta.json();
        } catch (parseErr) {
          parseFailed = true;
          datos = undefined;
          console.error("[CentroControlDirector] cargarCursos: response JSON parse failed", {
            status: respuesta.status,
            error: parseErr instanceof Error ? parseErr.message : String(parseErr),
          });
        }
        if (signal.aborted) {
          return;
        }
        if (respuesta.ok) {
          if (parseFailed) {
            if (!signal.aborted) {
              setMensajeListadoCursos(getMessage("centroControlDirector.coursesPayloadInvalid"));
            }
            return;
          }
          const rawList = Array.isArray(datos)
            ? datos
            : datos && typeof datos === "object" && Array.isArray(datos.cursos)
              ? datos.cursos
              : null;
          if (rawList === null) {
            console.error("[CentroControlDirector] cargarCursos: OK response but body is not a course array", {
              status: respuesta.status,
              datosType: datos == null ? String(datos) : typeof datos,
            });
            if (!signal.aborted) {
              setMensajeListadoCursos(getMessage("centroControlDirector.coursesPayloadInvalid"));
            }
            return;
          }
          const mapped = rawList
            .map((c) => {
              const idHex = extractMongoObjectIdHex(c && c._id);
              const titulo = typeof c?.titulo === "string" ? c.titulo.trim() : "";
              return { _id: idHex, titulo };
            })
            .filter((c) => c._id !== "");
          const sorted = mapped.sort((a, b) => {
            const ta = a.titulo.toLowerCase();
            const tb = b.titulo.toLowerCase();
            return ta.localeCompare(tb, "es");
          });
          if (!signal.aborted) {
            setMensajeListadoCursos("");
            setCursos(sorted);
          }
        } else {
          const err = datos && typeof datos.error === "string" ? datos.error : "";
          if (!signal.aborted) {
            setMensajeListadoCursos(err || getMessage("centroControlDirector.coursesLoadError"));
          }
          console.error("[CentroControlDirector] cargarCursos: non-OK response", {
            status: respuesta.status,
            bodyError: err || null,
          });
        }
      } catch (err) {
        if (signal.aborted) {
          return;
        }
        const isAbort = err instanceof DOMException && err.name === "AbortError";
        if (isAbort) {
          return;
        }
        console.error("[CentroControlDirector] cargarCursos: request failed", {
          error: err instanceof Error ? err.message : String(err),
        });
        if (!signal.aborted) {
          setMensajeListadoCursos(getMessage("centroControlDirector.coursesLoadError"));
        }
      }
    };
    cargarCursos();
    return () => {
      controller.abort();
    };
  }, []);

  const obtenerUsuarios = async () => {
    try {
      const { users, error: fetchError } = await fetchAllStaffUsers();
      if (fetchError) {
        setUsuariosFetchError(true);
        setMensaje(fetchError);
        setUsuarios([]);
        return;
      }
      setUsuariosFetchError(false);
      setMensaje("");
      setUsuarios(users);
    } catch {
      setUsuariosFetchError(true);
      setMensaje("Error de conexión al obtener usuarios");
      setUsuarios([]);
    } finally {
      setUsuariosLoaded(true);
    }
  };

  const usuariosFiltrados = useMemo(
    () =>
      usuarios.filter(
        (usuario) =>
          matchesUserTextSearch(usuario, busqueda) &&
          userEnrolledInCourseId(usuario, cursoFiltroId) &&
          userMatchesRoleFilter(usuario, rolFiltro),
      ),
    [usuarios, busqueda, cursoFiltroId, rolFiltro],
  );

  useEffect(() => {
    setSelectedUser((prev) => {
      if (!prev || prev._id == null) {
        return prev;
      }
      const prevId = String(prev._id);
      const stillVisible = usuariosFiltrados.some((u) => u != null && String(u._id) === prevId);
      return stillVisible ? prev : null;
    });
  }, [usuariosFiltrados]);

  // Al hacer clic en un usuario se selecciona (sin ser enlace)
  const handleSelectUser = (usuario) => {
    setSelectedUser(usuario);
      };

  // Manejar acciones del sidebar
  const handleSidebarAction = (action) => { 
    if (action === "registrar") {
        setSelectedUser(null);
        setModalAction("registrar");
        setModalVisible(true);
        return;
    }

    if (!selectedUser || !selectedUser._id) {
        alert("Por favor, selecciona un usuario válido de la lista.");
        return;
    }

    if (action === "GestionUsuarios") {
        console.log("🔹 Navegando a:", `/GestorUsuarios/${selectedUser._id}`);
        if (selectedUser && selectedUser._id) {
          console.log("🔹 Navegando a:", `/GestorUsuarios/${selectedUser._id}`);
          navigate(`/GestorUsuarios/${selectedUser._id}`);
        } else {
          alert("❌ No se ha seleccionado un usuario válido.");
        }        
        return;  // Asegurar que no siga ejecutando más código
    }

    setModalAction(action);
    setModalVisible(true);
};



  // Ejecutar acción confirmada
  const executeModalAction = async () => {
    if (modalAction === "eliminar") {
        await eliminarUsuario(selectedUser._id);
        setSelectedUser(null);
        obtenerUsuarios();
    } else if (modalAction === "cambiarRoles") {
        alert("Funcionalidad para cambiar roles en desarrollo.");
    } else if (modalAction === "GestorUsuarios") {
        if (selectedUser && selectedUser._id) {
            console.log("Navegando a:", `/GestorUsuarios/${selectedUser._id}`); // Verifica en la consola
            navigate(`/GestorUsuarios/${selectedUser._id}`); // Redirige a la página con el ID
        } else {
            alert("Error: No se ha seleccionado un usuario válido.");
        }
    } else if (modalAction === "registrar") {
        alert("Funcionalidad para registrar un nuevo usuario en desarrollo.");
    }
    setModalVisible(false);
    setModalAction("");
};



//Volver a pagina de inicio de cada usuario
const handleVolver = () => {
  navigate(getHomeRouteByRole(rol), { replace: true });
};

  return (
    <div className="cdc-container">
      
      <div className="cdc-main-container">
        <div className="cdc-content">
        <div className="titulo-director">
            <img
              src="/logos/logos_jerarquicos/1.logo_director.png"
              alt="Logo Director"
              className="logo-director"
            />
            <h1 className="texto-director">Centro de Control del Director</h1>
          </div>

          <div className="cdc-filters">
            <input
              type="text"
              className="form-control cdc-search"
              placeholder={getMessage("centroControlDirector.searchPlaceholder")}
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              aria-label={getMessage("centroControlDirector.searchPlaceholder")}
            />
            <label className="cdc-course-filter-label" htmlFor="cdc-course-filter">
              {getMessage("centroControlDirector.filterByCourseLabel")}
            </label>
            <ViewportSelect
              id="cdc-course-filter"
              className="form-control cdc-course-filter"
              value={cursoFiltroId}
              onChange={(e) => setCursoFiltroId(e.target.value)}
            >
              <option value="">{getMessage("centroControlDirector.allCourses")}</option>
              {cursos.map((curso, idx) => {
                const id = typeof curso._id === "string" ? curso._id : "";
                const titulo = curso.titulo !== "" ? curso.titulo : id;
                return (
                  <option key={id || `curso-${idx}`} value={id}>
                    {titulo}
                  </option>
                );
              })}
            </ViewportSelect>
            <label className="cdc-course-filter-label" htmlFor="cdc-role-filter">
              {getMessage("centroControlDirector.filterByRoleLabel")}
            </label>
            <ViewportSelect
              id="cdc-role-filter"
              className="form-control cdc-course-filter"
              value={rolFiltro}
              onChange={(e) => setRolFiltro(e.target.value)}
            >
              <option value="">{getMessage("centroControlDirector.allRoles")}</option>
              <option value="alumno">{getMessage("centroControlDirector.roleStudent")}</option>
              <option value="instructor">{getMessage("centroControlDirector.roleInstructor")}</option>
              <option value="director">{getMessage("centroControlDirector.roleDirector")}</option>
            </ViewportSelect>
          </div>
          {mensajeListadoCursos ? <p className="cdc-message">{mensajeListadoCursos}</p> : null}
          <div className="cdc-user-list-container">
            <h4>Lista de Usuarios</h4>
            {!usuariosLoaded ? null : usuariosFetchError ? (
              <p className="cdc-message">{mensaje}</p>
            ) : usuarios.length === 0 ? (
              <p>{getMessage("centroControlDirector.noUsersInTenant")}</p>
            ) : usuariosFiltrados.length === 0 ? (
              <p>{getMessage("centroControlDirector.noUsersMatchFilters")}</p>
            ) : (
              <ul className="cdc-user-list">
                {usuariosFiltrados.map((usuario) => (
                  <li
                    key={usuario._id}
                    className={`cdc-user-item ${selectedUser && selectedUser._id === usuario._id ? "selected" : ""}`}
                    onClick={() => handleSelectUser(usuario)}
                  >
                    <span className="cdc-user-item-label">
                      {usuario.nombre} ({usuario.rol})
                      {typeof usuario.dni === "string" && usuario.dni.trim() ? (
                        <span className="cdc-user-dni"> · {usuario.dni.trim()}</span>
                      ) : null}
                    </span>
                    {usuario.bloqueado ? (
                      <span className="cdc-user-status cdc-user-status--blocked">
                        {getMessage("centroControlDirector.userBlocked")}
                      </span>
                    ) : (
                      <span className="cdc-user-status cdc-user-status--active">
                        {getMessage("centroControlDirector.userActive")}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <ResponsiveActionSidebar
          id="centro-control-sidebar"
          className="cdc-sidebar"
          openClassName="cdc-sidebar--open"
          ariaLabel="Acciones del centro de control"
        >
          <h4>Acciones</h4>
          <ul className="cdc-sidebar-links">
            <li>
               <Link to="/Registrousuario" className="regusu-sidebar-button">
               Registro e invitación al pago
               </Link>
            </li>
            <li>
              <button className="cdc-sidebar-button" onClick={() => handleSidebarAction("GestorUsuarios")}>
                Gestionar Usuario
              </button>
            </li>
            <li>
              <button className="cdc-sidebar-button" onClick={() => handleSidebarAction("eliminar")}>
                Eliminar Usuario
              </button>
            </li>
            <li>
            <button className="mi-sidebar-button staff-sidebar-exit-button" onClick={handleVolver}>
                Salir
               </button>
            </li>
          </ul>
        </ResponsiveActionSidebar>
      </div>

      {modalVisible && (
        <div className="cdc-modal-backdrop">
          <div className="cdc-modal">
            <p>
              {modalAction === "eliminar" && selectedUser
                ? `¿Estás seguro de que deseas eliminar a ${selectedUser.nombre}?`
                : modalAction === "cambiarRoles" && selectedUser
                ? `¿Deseas cambiar el rol de ${selectedUser.nombre}?`
                : modalAction === "GestorUsuarios" && selectedUser
                ? `¿Deseas gestionar el usuario de ${selectedUser.nombre}?`
                : "Gestionar este Usuario."}
            </p>
            <div className="cdc-modal-buttons">
              <button className="btn btn-secondary" onClick={() => setModalVisible(false)}>
                Cancelar
              </button>
              <button className="btn btn-danger" onClick={executeModalAction}>
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CentroControlDirector;
